import { MeetingNoteStatus } from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useRegenerateMeetingNote } from 'src/meetings/hooks/useRegenerateMeetingNote';
import { useSetMeetingNoteReviewed } from 'src/meetings/hooks/useSetMeetingNoteReviewed';
import { MeetingNoteTab } from 'src/meetings/detail/tabs/MeetingNoteTab';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import type { CalendarEvent } from 'src/meetings/models';
import type { Transcript } from 'src/meetings/detail/tabs/transcript/types';
import type { MeetingScope } from 'src/meetings/scope';
import { ReviewableMeetingNote } from './ReviewableMeetingNote';

type Props = {
  event: CalendarEvent;
  transcript: Transcript | undefined;
  scope: MeetingScope;
};

export function FamilyMeetingNoteTab({ event, transcript, scope }: Props) {
  const { meetingNote, canRegenerate, regenerate, isRegenerating } =
    useRegenerateMeetingNote(event);
  const { setMeetingNoteReviewed, isLoading: isSettingReviewed } = useSetMeetingNoteReviewed(
    event.id,
  );

  const meetingNoteId = meetingNote?.id;
  const clientId = meetingNote?.clientId;

  const handleToggleReviewed = async (isReviewed: boolean) => {
    if (!meetingNoteId || !clientId) return;
    try {
      await setMeetingNoteReviewed({ clientId, meetingNoteId, isReviewed });
    } catch (error) {
      notifyAndLogError('Failed to update review status', 'Please try again later', error, {
        eventId: event.id,
        meetingNoteId,
      });
    }
  };

  if (meetingNote?.status === MeetingNoteStatus.READY && meetingNote.markdownContent) {
    return (
      <ReviewableMeetingNote
        title={event.title}
        markdownContent={meetingNote.markdownContent}
        onRegenerate={canRegenerate ? regenerate : undefined}
        isRegenerating={isRegenerating}
        isReviewed={meetingNote.isReviewed ?? false}
        onToggleReviewed={meetingNoteId && clientId ? handleToggleReviewed : undefined}
        isSettingReviewed={isSettingReviewed}
      />
    );
  }

  return <MeetingNoteTab event={event} transcript={transcript} scope={scope} />;
}
